import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../lib/cn.js';
const SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
};
/**
 * The one modal in the app.
 *
 * Before this existed there were eleven hand-rolled overlays. Ten of them had no `role="dialog"`,
 * no `aria-modal`, no accessible name, no Escape handler and no focus management, so a
 * keyboard-only user could not dismiss them - including the step-up prompt that collects a password
 * and an MFA code. `CommandPalette` was the only one that got it right, and this is its pattern
 * extracted.
 *
 * Two details worth keeping:
 *
 * 1. The backdrop closes only when the click LANDS on the backdrop (`e.target === e.currentTarget`).
 *    Every previous copy instead put `onClick={(e) => e.stopPropagation()}` on the panel, which is
 *    a click handler on a non-interactive element for no user-facing reason.
 * 2. Focus moves into the panel on open and returns to whatever was focused before on close, so
 *    dismissing a dialog does not dump the user at the top of the document.
 */
/** Everything Tab can reach inside the panel, in DOM order. */
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
export function Modal({ onClose, title, hideTitle, subtitle, size = 'md', elevated, align = 'center', className, hideCloseButton, children, }) {
    const panelRef = React.useRef(null);
    React.useEffect(() => {
        const previouslyFocused = document.activeElement;
        // Prefer the first focusable control (an input is usually what the user wants), else the panel.
        const focusTarget = panelRef.current?.querySelector('input:not([type="hidden"]), textarea, select, button:not([data-modal-close])') ?? panelRef.current;
        focusTarget?.focus();
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab')
                return;
            // Focus trap. `aria-modal="true"` TELLS assistive tech the rest of the page is inert, but it
            // does not make it so: without this, Tab walks straight out of the dialog and into the page
            // behind it, and a keyboard user is then interacting with content a screen reader has been
            // told does not exist. Escape and focus-return alone do not cover it.
            //
            // Queried on every Tab rather than cached on open because several of these dialogs fill in
            // asynchronously (upload progress, fetched permissions, a generated rule), so a list captured
            // at mount goes stale immediately.
            const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
            if (!nodes || nodes.length === 0)
                return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previouslyFocused?.focus?.();
        };
    }, [onClose]);
    return (_jsx("div", { 
        // role="presentation": the backdrop is a dismissal affordance, not a control. Escape and the
        // close button are the keyboard paths, so this needs no key handler of its own.
        role: "presentation", onClick: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, className: cn('fixed inset-0 flex justify-center bg-black/60 p-4', elevated ? 'z-[70]' : 'z-50', align === 'top' ? 'items-start pt-[15vh]' : 'items-center'), children: _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-label": title, tabIndex: -1, className: cn('w-full rounded-lg border border-white/10 bg-bg-raised p-5 outline-none', SIZES[size], className), children: [(!hideTitle || !hideCloseButton) && (_jsxs("div", { className: "flex items-start justify-between gap-3", children: [hideTitle ? (_jsx("span", {})) : (_jsx("h2", { className: "text-lg font-semibold text-text-primary", children: title })), !hideCloseButton && (_jsx("button", { type: "button", "data-modal-close": true, onClick: onClose, className: "shrink-0 text-text-muted transition-colors hover:text-text-primary", "aria-label": "Close", children: "\u2715" }))] })), subtitle ? _jsx("p", { className: "mt-1 truncate font-mono text-xs text-text-muted", children: subtitle }) : null, children] }) }));
}
//# sourceMappingURL=modal.js.map